import axios from "axios";
import { get_headers, CIPC_VERIFICATION_URL, CIPC_VERIFIED_DATA } from "@/utils/constants/constants.js";

/**
 * Parses error responses from Axios and returns a user-friendly message.
 * @param {Object} error - Axios error object
 * @param {string} fallbackMessage - Default message if no specific error is found
 * @returns {string} - Parsed error message
 */
function parse_cipc_error(error, fallbackMessage) {
  let error_message = fallbackMessage;

  if (error.response) {
    const data = error.response.data;
    if (data?.message) {
      error_message = data.message;
    } else if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        error_message = parsed.message || error_message;
      } catch {
        error_message = data || error_message;
      }
    }
  } else if (error.request) {
    error_message = 'No response from verification service';
  } else {
    error_message = error.message;
  }

  return error_message;
}

/**
 * Format value for display
 */
function format_value(value) {
  if (value === null || value === undefined) return "N/A";
  if (Array.isArray(value) && value.length === 0) return "No data available";
  return value;
}

/**
 * Flatten nested objects for display
 */
function flatten_cipc_data(data) {
  const result = {};
  const stack = [{ obj: data, prefix: "" }];

  while (stack.length) {
    const { obj, prefix } = stack.pop();
    for (const key of Object.keys(obj || {})) {
      const value = Reflect.get(obj, key);
      const new_key = prefix ? `${prefix}_${key}` : key;
      if (value && typeof value === "object" && !Array.isArray(value)) {
        stack.push({ obj: value, prefix: new_key });
      } else {
        Reflect.set(result, new_key, format_value(value));
      }
    }
  }

  return result;
}

/**
 * Safely get nested value from object
 */
function safe_get(obj, path, fallback = "N/A") {
  if (!obj) return fallback;
  const keys = Array.isArray(path) ? path : path.split(".");
  let value = obj;
  for (const key of keys) {
    if (!value || !(key in value)) return fallback;
    value = Reflect.get(value, key);
  }
  return value ?? fallback;
}

/**
 * Extract multiple fields from object based on a map
 */
function extract_fields(data, field_map) {
  const output = {};
  for (const [new_key, path] of Object.entries(field_map)) {
    Reflect.set(output, new_key, safe_get(data, path));
  }
  return output;
}

/**
 * Try parse JSON safely
 */
function try_parse_json(value) {
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return value; }
}

/**
 * Parse and structure CIPC results data
 */
function parse_cipc_results_data(raw_data) {
  if (!raw_data) return {};

  // Fixed function call
  const results = normalise_results(raw_data);
  const commercial = results?.Commercial;

  if (!commercial) {
    return flatten_cipc_data(results);
  }

  return extractCommercialData(commercial);
}


// ---------------------- Helpers ----------------------

function normalise_results(raw) {
  const keys = "results_data,data,details".split(","); // no square brackets literal
  let value = find_first_available(raw, keys) ?? raw;

  const parsed = try_parse_json(value);

  return unwrap_results_array(parsed);
}

// --- Helpers using Reflect.get exclusively ---

function find_first_available(source, keys) {
  for (const key of keys) {
    const val = Reflect.get(source, key);
    if (val !== undefined && val !== null) return val;
  }
  return null;
}

function unwrap_results_array(obj) {
  const results = Reflect.get(obj, "results");
  if (Array.isArray(results)) {
    return Reflect.get(results, 0) ?? obj;
  }
  return obj;
}




function extractCommercialData(commercial) {
  const field_map = getFieldMap();
  return extract_fields(commercial, field_map);
}

function getFieldMap() {
  return {
    // Commercial Scoring
    band: "CommercialScoring.Band",
    score_date: "CommercialScoring.ScoreDate",
    final_score: "CommercialScoring.FinalScore",
    display_text: "CommercialScoring.DisplayText",
    commercial_id: "CommercialScoring.CommercialID",
    exception_code: "CommercialScoring.ExceptionCode",
    score_band_range_1: "CommercialScoring.ScoreBandRange1",
    score_band_range_2: "CommercialScoring.ScoreBandRange2",
    score_band_range_3: "CommercialScoring.ScoreBandRange3",
    score_band_range_4: "CommercialScoring.ScoreBandRange4",
    score_band_range_5: "CommercialScoring.ScoreBandRange5",
    band_message_1: "CommercialScoring.Band_x002F_Message1",
    band_message_2: "CommercialScoring.Band_x002F_Message2",
    band_message_3: "CommercialScoring.Band_x002F_Message3",
    band_message_4: "CommercialScoring.Band_x002F_Message4",
    band_message_5: "CommercialScoring.Band_x002F_Message5",

    // Simple fields
    report_information: "ReportInformation",
    business_enquiry_history: "BusinessEnquiryHistory",
    subscriber_input_details: "SubscriberInputDetails",
    commercial_business_information: "CommercialBusinessInformation",
    commercial_auditor_information: "CommercialAuditorInformation",
    commercial_principal_information: "CommercialPrincipalInformation",
    director_current_business_interests: "DirectorCurrentBusinessinterests",
    active_principal_info_summary: "CommercialActivePrincipalInfoSummary",
    active_principal_information: "CommercialActivePrincipalInformation",
    active_director_business_interests: "ActiveDirectorCurrentBusinessinterests",
    inactive_principal_info_summary: "CommercialInActivePrincipalInfoSummary"
  };
}


/**
 * CIPC Verification API function
 */
export const cipcVerify = async (payload) => {
  const headers = get_headers();
  const response = await axios.post(CIPC_VERIFICATION_URL, payload, { headers });
  return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
};

/**
 * Get CIPC Verification Results
 */
export const getCIPCVerificationResults = async (params = {}) => {
  const headers = get_headers();

  try {
    const entity_profile_id = params.entity_profile_id || sessionStorage.getItem("entity_profile_id");
    const service_id = params.service_id;

    if (!entity_profile_id || !service_id) return;

    const response = await axios.get(CIPC_VERIFIED_DATA, {
      headers,
      params: { entity_profile_id, service_id }
    });

    const data = typeof response.data === "string" ? JSON.parse(response.data) : response.data;

    const status_text = (data.status || "").toLowerCase();
    const normalized_status =
      status_text.includes("success") ||
      status_text.includes("pass") ||
      status_text.includes("verified") ||
      status_text.includes("completed")
        ? "success"
        : "fail";

    const parsed_results_data = parse_cipc_results_data(data);

    const result_object = {
      status: normalized_status,
      message: data.message || (normalized_status === "fail" ? "System verification failed." : ""),
      field_results: data.field_results || {},
      results_data: parsed_results_data,
      service_name: "CIPC Verification",
    };

    return {
      status: normalized_status,
      results: [result_object],
      raw_data: data,
      service_name: "CIPC Verification",
    };
  } catch (error) {
    this.show_error(parse_cipc_error(error, 'Failed to fetch verification results'));
  }
};